import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import StorableChip from 'inventory/chips/StorableChip';
import {FrozenEntityChipData} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  BioentityContent,
  ContainerSource,
  ReagentAssignmentGroup,
  ReagentType,
  Source,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {getTestOrderIdsForSource} from 'samples/plate-maps/PlateMapUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';

interface BioentityOrEmptyContainerSourceChipProps {
  chipSize: ChipSize;
  onRemove?: () => void;
  reagent: Source | BioentityContent;
  reagentGroup: ReagentAssignmentGroup;
  frozenData?: FrozenEntityChipData;
  testOrderInfoById: TestOrderInfoById;
}

function getDilutionFactorText(
  reagent: Source | BioentityContent,
  testOrderInfoById: TestOrderInfoById
): string | null {
  if (!BenchlingConfig.SAMPLE_TESTS_ENABLE) {
    return null;
  }
  const testOrderIds: Array<string> =
    reagent.type === ReagentType.SOURCE_CONTAINABLE || reagent.type === ReagentType.SOURCE_CONTAINER
      ? [...getTestOrderIdsForSource(reagent)]
      : (reagent.testOrders ?? []).map(({id}) => id);

  const dilutionText = [
    ...new Set(
      testOrderIds
        .map((id) => testOrderInfoById?.[id]?.dilutionFactor)
        .filter((dilutionFactor): dilutionFactor is number => dilutionFactor != null)
    ),
  ].join(', ');
  return dilutionText.length > 0 ? `Test dilution factor: ${dilutionText}` : null;
}

/**
 * A wrapper around BenchChip for a reagent without any contents, e.g. a bare bioentity or an empty container
 * @param chipSize What size of chip to render
 * @param onRemove A function to be called when the reagent is removed; if omitted, no remove button is
 *                 displayed
 * @param reagent The reagent to display a chip for
 * @param reagentGroup The group the reagent belongs to
 * @param frozenData Fields necessary for rendering a frozen chip. If present, a bare Chip will be rendered
 *                   rather than a BenchChip.
 * @param testOrderInfoById Test order-dilution mapping to render for well contents & source entities.
 * @constructor
 */
function BioentityOrEmptyContainerSourceChip({
  chipSize,
  onRemove,
  reagent,
  reagentGroup,
  frozenData,
  testOrderInfoById,
}: BioentityOrEmptyContainerSourceChipProps): JSX.Element {
  let chip: JSX.Element;
  // Frozen metadata is passed iff this chip is in the transfer modal. Make it clickable if so.
  if (frozenData == null || frozenData.isInaccessible) {
    chip = (
      <BenchChip
        size={chipSize}
        id={reagent.id}
        onRemove={onRemove}
        isLink={false}
        getSecondaryText={() => getDilutionFactorText(reagent, testOrderInfoById)}
      />
    );
  } else {
    chip = (
      <BenchChip
        size={chipSize}
        id={reagent.id}
        getSecondaryText={() => getDilutionFactorText(reagent, testOrderInfoById)}
        onRemove={onRemove}
        displayNameOverride={frozenData.name}
        iconOverride={frozenData.iconId}
      />
    );
  }
  return (
    <div className={css(styles.wrapper)} {...getTestProps({component: 'ReagentChip', element: 'root'})}>
      <div
        className={css(styles.swatch)}
        style={{backgroundColor: reagentGroup.color}}
        {...getTestProps({component: 'ReagentChip', element: 'swatch', key: reagent.id})}
        data-amp-safe={false}
      />
      {chip}
    </div>
  );
}

interface ContainerSourceWithContentsChipProps {
  chipSize: ChipSize;
  onRemove?: () => void;
  reagent: ContainerSource;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  frozenChipDisplayOverrides?: {[id: string]: FrozenEntityChipData};
  testOrderInfoById: TestOrderInfoById;
}

/**
 * A wrapper around StorableChip for a container reagent with contents. Injects BioentityOrEmptyContainerSourceChip
 * into the StorableChip fancy popover, so that reagent group swatches are displayed on hover.
 * @param chipSize What size of chip to render
 * @param onRemove A function to be called when the reagent is removed
 * @param reagent The reagent to display a chip for
 * @param reagentGroupsById A mapping of reagent group ids to reagent groups
 * @param frozenChipDisplayOverrides Fields necessary for rendering a frozen chip. If present, a bare Chip
 *                                   will be rendered rather than a BenchChip.
 * @param testOrderInfoById Test order-dilution mapping to render for well contents.
 * @constructor
 */
function ContainerSourceWithContentsChip({
  chipSize,
  onRemove,
  reagent,
  reagentGroupsById,
  frozenChipDisplayOverrides,
  testOrderInfoById,
}: ContainerSourceWithContentsChipProps): JSX.Element {
  const contentReagentsById = objectFromEntries(reagent.contents.map((content) => [content.id, content]));

  const popoverContainedItemRenderer = (id: string): JSX.Element => {
    const reagent = contentReagentsById[id];
    // Possible if the content is inaccessible, and was excluded from search results
    if (!reagent) {
      return (
        <div key={id} className={css(styles.popoverChip)}>
          <BenchChip size={chipSize} id={id} isLink={false} />
        </div>
      );
    }

    return (
      <div key={id} className={css(styles.popoverChip)}>
        <BioentityOrEmptyContainerSourceChip
          chipSize={chipSize}
          reagent={reagent}
          reagentGroup={reagentGroupsById[reagent.groupId]}
          frozenData={frozenChipDisplayOverrides?.[reagent.id]}
          testOrderInfoById={testOrderInfoById}
        />
      </div>
    );
  };

  let storableChip: JSX.Element;
  const storableFrozenData = frozenChipDisplayOverrides?.[reagent.id];
  // Frozen metadata is passed iff this chip is in the transfer modal. Make it clickable if so.
  if (storableFrozenData == null || storableFrozenData.isInaccessible) {
    storableChip = (
      <StorableChip
        size={chipSize}
        id={reagent.id}
        onRemove={onRemove}
        popoverContainedItemRenderer={popoverContainedItemRenderer}
        isLink={false}
        getSecondaryText={() => getDilutionFactorText(reagent, testOrderInfoById)}
      />
    );
  } else {
    storableChip = (
      <StorableChip
        size={chipSize}
        id={reagent.id}
        onRemove={onRemove}
        popoverContainedItemRenderer={popoverContainedItemRenderer}
        displayNameOverride={storableFrozenData.name}
        iconOverride={storableFrozenData.iconId}
        getSecondaryText={() => getDilutionFactorText(reagent, testOrderInfoById)}
      />
    );
  }

  return (
    <div className={css(styles.wrapper)} {...getTestProps({component: 'ReagentChip', element: 'root'})}>
      {Object.values(contentReagentsById).map((content) => (
        <div
          className={css(styles.swatch)}
          key={content.id}
          style={{backgroundColor: reagentGroupsById[content.groupId].color}}
          {...getTestProps({component: 'ReagentChip', element: 'swatch', key: content.id})}
          data-amp-safe={false}
        />
      ))}
      {storableChip}
    </div>
  );
}

export interface ReagentChipProps {
  chipSize?: ChipSize;
  onRemove?: () => void;
  reagent: Source | BioentityContent;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  frozenChipDisplayOverrides?: {[id: string]: FrozenEntityChipData};
  testOrderInfoById: TestOrderInfoById;
}

/**
 * A chip component to be used when filling plates. Wraps BenchChip/StorableChip and displays swatch(es) of
 * color next to the chip corresponding to the reagent group color(s) of the constituent reagent(s).
 * @param [chipSize=ChipSize.MINI] What size of chip to render
 * @param onRemove A function to be called when the reagent is removed
 * @param reagent The reagent to display a chip for
 * @param reagentGroupsById A mapping of reagent group ids to reagent groups
 * @param frozenChipDisplayOverrides Fields necessary for rendering a frozen chip. If present, a bare Chip
 *                                   will be rendered rather than a BenchChip.
 * @param testOrderInfoById Test order-dilution mapping to render for well contents & source entities..
 * @constructor
 */
export default function ReagentChip({
  chipSize = ChipSize.MINI,
  onRemove,
  reagent,
  reagentGroupsById,
  frozenChipDisplayOverrides,
  testOrderInfoById,
}: ReagentChipProps): JSX.Element {
  return (
    <>
      {reagent.type === ReagentType.SOURCE_CONTAINABLE ||
      reagent.type === ReagentType.WELL_CONTENT ||
      reagent.contents.length === 0 ? (
        <BioentityOrEmptyContainerSourceChip
          chipSize={chipSize}
          onRemove={onRemove}
          reagent={reagent}
          reagentGroup={reagentGroupsById[reagent.groupId]}
          frozenData={frozenChipDisplayOverrides?.[reagent.id]}
          testOrderInfoById={testOrderInfoById}
        />
      ) : (
        <ContainerSourceWithContentsChip
          chipSize={chipSize}
          onRemove={onRemove}
          reagent={reagent}
          reagentGroupsById={reagentGroupsById}
          frozenChipDisplayOverrides={frozenChipDisplayOverrides}
          testOrderInfoById={testOrderInfoById}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  swatch: {
    width: '4px',
    minWidth: '4px',
    marginRight: '4px',
  },
  wrapper: {
    display: 'flex',
  },
  popoverChip: {
    ':not(:last-child)': {
      paddingBottom: '4px',
    },
  },
});
